import clsx from "clsx";
import {
  FC,
  Fragment,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  InAppLink,
  PopOver,
  StickyScrollProvider,
} from "@tsmono/react/components";
import { useEventListener } from "@tsmono/react/hooks";
import { Icon } from "@tsmono/react/icons";
import { isEditableTarget } from "@tsmono/util";

import styles from "./FocusTurnView.module.css";
import { GoToTurnBar } from "./GoToTurnBar";
import type { FocusTurnNavigation } from "./hooks/useFocusTurnNavigation";
import selectorStyles from "./timeline/components/TimelineSelector.module.css";
import { TranscriptVirtualList } from "./TranscriptVirtualList";
import type { EventPanelCallbacks } from "./types";

interface FocusTurnViewProps {
  nav: FocusTurnNavigation;
  eventId: string | null;
  /**
   * Optional chrome (e.g. the breadcrumb navbar) rendered above the focus bar,
   * outside the scroll container (a sibling, not nested in it).
   */
  header?: ReactNode;
  /**
   * Applied to the root. The default root height is 100vh (inspect's panels
   * own the viewport); a host whose app chrome sits above the page (scout's
   * ProjectBar) must override the height or the root overflows its container
   * and mount-time scrolling clips the header.
   */
  className?: string;
  /**
   * Exit focus mode: navigate back to the normal transcript at the focused
   * event (use `nav.resolvedEventId` as the deep-link target). Omitting it
   * hides the exit control.
   */
  onExit?: () => void;
  /**
   * Where exiting goes. When set, the exit control is a link, so
   * cmd/ctrl/middle-click open the transcript in a new tab; `onExit` still
   * handles plain clicks (and Esc/f).
   */
  exitHref?: string;
  /**
   * Sample/transcript-level error, surfaced as a persistent strip under the
   * header on EVERY focused turn (only the last turn's slice carries the
   * actual error card). Clicking jumps to the last turn.
   */
  error?: { label: string; message: string };
}

/**
 * Renders focus mode for a single turn: a fixed secondary header (lane
 * breadcrumb; turn label, turn/lane navigation and exit control on the right)
 * above the turn's events,
 * fully expanded, with the transcript's own renderer but without the list's
 * card/gutter chrome. Shared by the inspect and scout focus pages, which
 * supply their own loading/error chrome.
 */
export const FocusTurnView: FC<FocusTurnViewProps> = ({
  nav,
  eventId,
  header,
  className,
  onExit,
  exitHref,
  error,
}) => {
  const {
    scrollRef,
    listHandle,
    slice,
    checks,
    turnInfo,
    totalTurns,
    onPrev,
    onNext,
    canStepNext,
    canStepPrev,
    goToTurn,
    laneCrumbs,
    laneOptions,
    focusTab,
  } = nav;

  const [laneMenuOpen, setLaneMenuOpen] = useState(false);

  // Idempotent: HashRouter unmount isn't synchronous, so a second Esc/f/click before it would double-push history, needing two Backs to leave.
  const handleExit = useMemo(() => {
    if (!onExit) return undefined;
    let exited = false;
    return () => {
      if (exited) return;
      exited = true;
      onExit();
    };
  }, [onExit]);

  // Esc/f exit focus mode (f mirrors the transcript's enter-focus binding); lane picker claims both keys first, and typing targets are ignored. `f` ignores modifiers so Cmd+F find is intact.
  useEventListener(handleExit ? document : null, "keydown", (e) => {
    // Exact-case `f`, matching the transcript's enter-focus binding — so
    // Shift+F/Caps Lock neither enters nor exits, never just one of the two.
    const isExitKey =
      e.key === "Escape" ||
      (e.key === "f" && !e.metaKey && !e.ctrlKey && !e.altKey);
    if (!isExitKey) return;
    if (isEditableTarget(document.activeElement)) return;
    e.preventDefault();
    if (laneMenuOpen) {
      setLaneMenuOpen(false);
      return;
    }
    handleExit?.();
  });

  // Reset scroll to the top of each newly focused turn. The slice changes on
  // j/k but the scroll container keeps its previous offset, so a tall turn
  // followed by a shorter one would open mid-scroll. Key on resolvedEventId so
  // a tool/sandbox target resolving to the same turn doesn't jump spuriously.
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [nav.resolvedEventId, scrollRef]);

  const laneButtonRef = useRef<HTMLButtonElement | null>(null);
  const showLanePicker = laneOptions.length > 1;

  // The focus page shares one URL-backed tab (by name) across events, so
  // navigating between turns keeps the same tab open; events without the
  // shared tab fall back to their first tab.
  const eventCallbacks = useMemo<EventPanelCallbacks>(
    () => ({
      getSelectedTab: (_eventNodeId: string, tabNames: string[]) =>
        tabNames.includes(focusTab.tab) ? focusTab.tab : tabNames[0],
      onSelectTab: (_eventNodeId: string, tabName: string) =>
        focusTab.setTab(tabName),
      // User tab click: reset to the top of the focused turn so the new tab
      // shows from its start (mirrors the per-turn scroll reset above).
      onTabSelected: () => scrollRef.current?.scrollTo({ top: 0 }),
    }),
    [focusTab, scrollRef]
  );

  return (
    <div className={clsx(styles.root, className)}>
      {header}
      {error && (
        <button
          type="button"
          className={styles.errorStrip}
          onClick={() => goToTurn(totalTurns)}
          title="Jump to the last turn (the error details render after it)"
        >
          <span className={styles.errorStripLabel}>{error.label}</span>
          <span className={styles.errorStripMessage}>{error.message}</span>
        </button>
      )}
      <div className={clsx("text-size-smaller", styles.focusBar)}>
        <div className={styles.crumbs}>
          {laneCrumbs.map((crumb, i) => {
            const isLast = i === laneCrumbs.length - 1;
            // Anchor the picker on the ROOT crumb: its menu lists the root-level
            // lane siblings, so it must sit at that level — matching the
            // transcript's root TimelineSelector (`auditor ˅ / main`), not the
            // last crumb whose level the options don't belong to.
            const isPicker = i === 0 && showLanePicker;
            return (
              <Fragment key={i}>
                {i > 0 && (
                  <Icon name="chevronRight" className={styles.crumbSeparator} />
                )}
                {isPicker ? (
                  <button
                    ref={laneButtonRef}
                    type="button"
                    className={selectorStyles.selectorButton}
                    title="Select agent"
                    aria-label={`Select agent (current: ${crumb.label})`}
                    aria-haspopup="listbox"
                    aria-expanded={laneMenuOpen}
                    onClick={() => setLaneMenuOpen((open) => !open)}
                  >
                    {crumb.label}
                    <Icon
                      name="chevronDown"
                      className={selectorStyles.chevron}
                    />
                  </button>
                ) : isLast ? (
                  <span className={styles.laneName}>{crumb.label}</span>
                ) : crumb.onSelect ? (
                  <button
                    type="button"
                    className={styles.crumbButton}
                    title={`Go to ${crumb.label}`}
                    onClick={crumb.onSelect}
                  >
                    {crumb.label}
                  </button>
                ) : (
                  <span>{crumb.label}</span>
                )}
              </Fragment>
            );
          })}
        </div>
        <div className={styles.controls}>
          {turnInfo && (
            <span className={styles.turnLabel}>
              {nav.followingLatest ? "following latest turn " : "turn "}
              {turnInfo.turnNumber}/{turnInfo.totalTurns}
            </span>
          )}
          <button
            type="button"
            className={styles.button}
            title="Next turn (j)"
            aria-label="Next turn"
            disabled={!canStepNext}
            onClick={onNext}
          >
            <Icon name="chevronDown" />
          </button>
          <button
            type="button"
            className={styles.button}
            title="Previous turn (k)"
            aria-label="Previous turn"
            disabled={!canStepPrev}
            onClick={onPrev}
          >
            <Icon name="chevronUp" />
          </button>
          {handleExit && (
            <>
              <span className={styles.divider} />
              <InAppLink
                href={exitHref}
                onNavigate={handleExit}
                className={styles.button}
                title="Exit focus mode (Esc or f)"
                aria-label="Exit focus mode"
              >
                <Icon name="focusExit" />
              </InAppLink>
            </>
          )}
        </div>
      </div>
      {showLanePicker && (
        <PopOver
          id="focus-lane-picker"
          isOpen={laneMenuOpen}
          setIsOpen={setLaneMenuOpen}
          // eslint-disable-next-line react-hooks/refs -- positionEl accepts null; PopOver handles this in effects
          positionEl={laneButtonRef.current}
          placement="bottom-start"
          hoverDelay={-1}
        >
          <div className={clsx("text-size-smaller", styles.laneMenu)}>
            {laneOptions.map((option, i) => (
              <button
                key={i}
                type="button"
                className={clsx(
                  styles.laneMenuItem,
                  option.isCurrent && styles.laneMenuItemCurrent
                )}
                style={{ paddingLeft: `${0.4 + option.depth * 0.9}em` }}
                onClick={() => {
                  option.onSelect();
                  setLaneMenuOpen(false);
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </PopOver>
      )}
      <div className={styles.container} ref={scrollRef}>
        <StickyScrollProvider value={{ stickyTop: 0, scrollRef }}>
          {totalTurns > 0 && (
            <GoToTurnBar totalTurns={totalTurns} onJump={goToTurn} />
          )}
          <TranscriptVirtualList
            id={`event-${eventId}`}
            listHandle={listHandle}
            eventNodes={slice}
            scrollRef={scrollRef}
            disableVirtualization={true}
            // The slice can start inside an agent span; indent relative to the
            // focused turn, not the absolute transcript nesting.
            relativeIndent={true}
            eventCallbacks={eventCallbacks}
            // Slice starts at the focused model, so the per-node back-scan would
            // compute hasToolEvents=false and the model would inline + expand its
            // input tool messages. Force true so it renders the compact summary.
            // The slice also lacks the tool events that showed those messages,
            // so per-call coverage applies to the model's own calls only.
            eventNodeContext={{
              hasToolEvents: true,
              ...checks,
              inputShownElsewhere: true,
            }}
          />
        </StickyScrollProvider>
      </div>
    </div>
  );
};
